<script setup>
import { computed } from 'vue';
import { readBootstrap } from '../../shared/bootstrap.js';
import CategoryCartItemGallery from '../category/components/CategoryCartItemGallery.vue';
import CategoryRentalBanner from '../category/components/CategoryRentalBanner.vue';
import ProductCartItem from './components/ProductCartItem.vue';
import ProductPageTabs from './components/ProductPageTabs.vue';
import { productCartItemLabels, productCartItemPickupLocations, productCartItemTrustCards } from './product-cart-item.config.js';
import {
  formatReviewLabel,
  normalizeProductPageBoot
} from './helpers/product-page-data.js';

const boot = readBootstrap(
  typeof document !== 'undefined' ? document.getElementById('vsb-vue-product') : null,
  'vsb-product-bootstrap'
);

const page = computed(() => normalizeProductPageBoot(boot));
const product = computed(() => page.value.product);

const breadcrumbs = computed(() =>
  page.value.breadcrumbs.filter((b) => b.text && b.text !== 'Home')
);

const ancestorCrumbs = computed(() => breadcrumbs.value.slice(0, -1));
const currentCrumb = computed(() => {
  const last = breadcrumbs.value[breadcrumbs.value.length - 1];
  if (!last) return '';
  if (product.value.name && last.text === product.value.name) return '';
  return last.text;
});

const rental = computed(() => page.value.rentalPeriod || {});
const showRentalBanner = computed(() => Boolean(rental.value.show && rental.value.label));

const badgeText = computed(() =>
  product.value.badge === 'popular' ? productCartItemLabels.badgePopular : ''
);

const reviewLabel = computed(() =>
  formatReviewLabel(productCartItemLabels.reviewsTemplate, product.value.reviewCount)
);

const reserveFormBase = computed(() => page.value.reserveFormBase);

const labels = computed(() => ({
  ...productCartItemLabels,
  ...(page.value.labels || {})
}));

const trustCards = computed(() =>
  page.value.trustCards.length ? page.value.trustCards : productCartItemTrustCards
);
</script>

<template>
  <div class="vsb-product-page">
    <nav v-if="ancestorCrumbs.length || currentCrumb" class="vsb-product-page__breadcrumb" aria-label="Breadcrumb">
      <a v-for="(crumb, i) in ancestorCrumbs" :key="i" :href="crumb.href">{{ crumb.text }}</a>
      <span v-if="currentCrumb" class="vsb-product-page__breadcrumb-current">{{ currentCrumb }}</span>
    </nav>

    <CategoryRentalBanner
      v-if="showRentalBanner"
      :label="rental.label"
      :clear-href="rental.clearHref"
    />

    <header class="vsb-product-page__header">
      <h1 class="vsb-product-page__title">{{ product.name }}</h1>
      <div
        v-if="product.rating > 0 || product.reviewCount > 0"
        class="vsb-product-page__rating"
      >
        <span class="vsb-product-page__stars" aria-hidden="true">★★★★★</span>
        <span>{{ product.rating.toFixed(1).replace('.', ',') }} {{ reviewLabel }}</span>
      </div>
    </header>

    <div class="vsb-product-page__layout">
      <div class="vsb-product-page__main">
        <CategoryCartItemGallery
          :images="product.images"
          :alt="product.name"
          :badge-text="badgeText"
        />

        <p v-if="product.shortDescription" class="vsb-product-page__intro">
          {{ product.shortDescription }}
        </p>

        <ul class="vsb-product-page__features">
          <li v-for="(usp, i) in product.featureUsps" :key="i">
            <span aria-hidden="true">✓</span>
            {{ usp.text }}
          </li>
        </ul>

        <ProductPageTabs
          :attributes="product.attributes"
          :description-html="product.descriptionHtml"
          :review-count="product.reviewCount"
          :review-status="page.reviewStatus !== false"
          :phone="page.phone"
          :help-cta="labels.helpCta || 'of reserveer direct online.'"
        />
      </div>

      <ProductCartItem
        class="vsb-product-page__sidebar"
        :product="product"
        :reserve-form-base="reserveFormBase"
        :rental-period-url-suffix="page.rentalPeriodUrlSuffix"
        :pickup-locations="page.pickupLocations.length ? page.pickupLocations : productCartItemPickupLocations"
        :default-pickup-location="page.defaultPickupLocation"
        :availability-mode="page.availabilityMode"
        :labels="labels"
        :trust-cards="trustCards"
        :show-rental-dates="page.showRentalDates"
      />
    </div>
  </div>
</template>

<style scoped>
.vsb-product-page {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0 2rem;
  box-sizing: border-box;
}

.vsb-product-page__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.vsb-product-page__breadcrumb a {
  color: #64748b;
  text-decoration: none;
}

.vsb-product-page__breadcrumb a:hover {
  color: #0b3d91;
  text-decoration: underline;
}

.vsb-product-page__breadcrumb a::after {
  content: '›';
  margin-left: 0.35rem;
  color: #94a3b8;
  text-decoration: none;
}

.vsb-product-page__breadcrumb-current {
  color: #94a3b8;
}

.vsb-product-page__header {
  margin: 0 0 1.15rem;
}

.vsb-product-page__title {
  margin: 0;
  font-size: clamp(1.75rem, 4vw, 2.45rem);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: #0b3d91;
}

.vsb-product-page__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: 2rem;
  align-items: start;
}

.vsb-product-page__main {
  min-width: 0;
}

.vsb-product-page__sidebar {
  position: sticky;
  top: 1rem;
}

.vsb-product-page__rating {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.45rem;
  font-size: 0.9rem;
  color: #64748b;
}

.vsb-product-page__stars {
  color: #facc15;
  letter-spacing: -0.05em;
}

.vsb-product-page__intro {
  margin: 0.75rem 0 0;
  line-height: 1.6;
  color: #475569;
  font-size: 0.95rem;
}

.vsb-product-page__features {
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1.25rem;
  font-size: 0.85rem;
  color: #475569;
}

.vsb-product-page__features span {
  color: #3d9916;
  margin-right: 0.2rem;
}

@media (max-width: 960px) {
  .vsb-product-page__layout {
    grid-template-columns: 1fr;
  }

  .vsb-product-page__sidebar {
    position: static;
    order: -1;
  }
}
</style>
